import React, {useEffect, useRef, useState} from 'react';
import useOutsideClick from 'hooks/useOutsideClick';
import {useDispatch, useSelector} from 'react-redux';
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useParams
} from 'react-router-dom';
import cn from 'classnames';
import Headline from 'components/Headline/styles';
import {DashboardContent} from 'components/DashboardContent/styles';
import {DashboardHeader} from 'components/DashboardHeader/styles';
import UpsellPage from 'routes/App/emailMarketingV2/components/UpsellPage';
import MarketingContactUsModal from 'routes/App/emailMarketing/components/Modals/MarketingContactUsModal';
import ResultPage from 'routes/App/emailMarketingV2/components/ResultPage';
import CampaignsTable from 'routes/App/emailMarketingV2/components/CampaignsTable';
import CampaignDetail from 'routes/App/emailMarketingV2/components/CampaignDetail';
import {ButtonInfo, ButtonOutline} from 'components/buttons';
import {
  EmailMarketingSubHeadline,
  SettingsMenu,
  SettingsMenuItem,
  SettingsMenuWrapper
} from 'routes/App/emailMarketingV2/components/styles';
import InformationModal from 'routes/App/emailMarketing/components/Modals/InformationModal';
import {EmailMarketingSpinner} from 'routes/App/emailMarketing/components/styles';
import Loading from 'components/shared/Utils/Loading';
import {
  OPEN_INFORMATION_MODAL,
  GET_EMAIL_MARKETING_TRIAL_DATE,
  SET_EMAIL_MARKETING_TRIAL_DATE
} from 'store/emailMarketing/action';
import sendGtmEvent from 'helpers/services/utils/gtm';
import {EMAIL_MARKETING_TRIAL_DATE_EXPIRED} from 'helpers/constants/emailMarketing';

const CAMPAIGN_DETAIL_PATH = '/marketing-v2/campaigns/:campaignId';

const CampaignDetailRoute = () => {
  const history = useHistory();
  const location = useLocation();
  const {campaignId} = useParams();
  const sourceType = new URLSearchParams(location.search).get('sourceType');

  return (
    <CampaignDetail
      campaignId={campaignId}
      sourceType={sourceType}
      onBack={() => history.push('/marketing-v2')}
    />
  );
};

const EmailMarketingV2 = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    isOpenModal,
    isOpenInformationModal,
    spinner,
    emailMarketingTrialDate,
    emailMarketingTrialDateStatus
  } = useSelector((state) => state.emailMarketingReducer);
  const {activeLocation} = useSelector((state) => state.user);

  useEffect(() => {
    dispatch(GET_EMAIL_MARKETING_TRIAL_DATE());
    if (activeLocation?._id) {
      sendGtmEvent('view-marketing-tab', {
        type: activeLocation.AllowedServiceFeatures.includes(
          'show_marketing_results'
        )
          ? 'paying'
          : 'non-paying',
        location_name: activeLocation.FullName,
        location_id: activeLocation._id
      });
    }

    return () => {
      dispatch(SET_EMAIL_MARKETING_TRIAL_DATE({startDate: null, status: null}));
    };
  }, [activeLocation._id]);

  const showsMarketingResults = activeLocation.AllowedServiceFeatures.includes(
    'show_marketing_results'
  );
  const isTrialWithResults =
    activeLocation.AllowedServiceFeatures.includes('email_marketing_pcs') &&
    (emailMarketingTrialDate !== null ||
      emailMarketingTrialDateStatus === EMAIL_MARKETING_TRIAL_DATE_EXPIRED);
  const hasResults = showsMarketingResults || isTrialWithResults;

  const settingsRef = useRef(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useOutsideClick(settingsRef, () => setSettingsOpen(false), settingsOpen);

  const openCampaign = (campaign) => {
    const query = campaign?.sourceType
      ? `?sourceType=${encodeURIComponent(campaign.sourceType)}`
      : '';
    history.push(`/marketing-v2/campaigns/${campaign.campaignId}${query}`);
  };

  return (
    <DashboardContent
      className={cn('DashboardContent', {
        centerSpinner: spinner
      })}
    >
      <Switch>
        <Route exact path={CAMPAIGN_DETAIL_PATH}>
          {hasResults ? <CampaignDetailRoute /> : <UpsellPage />}
        </Route>
        <Route>
          {(hasResults || spinner) && (
            <DashboardHeader
              className={cn('AlwaysFlex', {
                flex0: spinner
              })}
            >
              <Headline className='mTop5'>Email Marketing</Headline>
              {!showsMarketingResults && isTrialWithResults && (
                <ButtonInfo
                  onClick={() => dispatch(OPEN_INFORMATION_MODAL(true))}
                  className='Outline ButtonInfoDrop'
                />
              )}
              {hasResults && (
                <SettingsMenuWrapper ref={settingsRef}>
                  <ButtonOutline
                    className='ButtonMedium'
                    onClick={() => setSettingsOpen((prev) => !prev)}
                    aria-expanded={settingsOpen}
                  >
                    <span>Settings</span>
                  </ButtonOutline>
                  {settingsOpen && (
                    <SettingsMenu>
                      <SettingsMenuItem
                        type='button'
                        onClick={() => history.push('/store-appearance')}
                      >
                        Manage Custom Colors
                      </SettingsMenuItem>
                    </SettingsMenu>
                  )}
                </SettingsMenuWrapper>
              )}
            </DashboardHeader>
          )}
          {hasResults && (
            <EmailMarketingSubHeadline>
              Track your email marketing performance and campaign results.
            </EmailMarketingSubHeadline>
          )}
          {hasResults && (
            <>
              <ResultPage />
              {!spinner && <CampaignsTable onOpenCampaign={openCampaign} />}
            </>
          )}
          {!hasResults && spinner && (
            <EmailMarketingSpinner>
              <Loading />
            </EmailMarketingSpinner>
          )}
          {!hasResults && !spinner && <UpsellPage />}
        </Route>
      </Switch>
      <MarketingContactUsModal openModal={isOpenModal} />
      <InformationModal openModal={isOpenInformationModal} />
    </DashboardContent>
  );
};

export default EmailMarketingV2;
